import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Wrench, Github, Twitter, Sun, Moon } from "lucide-react";
import useTheme from "../hooks/useTheme";

export default function Layout({ children }) {
  const loc = useLocation();
  const { theme, toggle } = useTheme();
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
              <Wrench size={18} strokeWidth={2} />
            </div>
            <span className="font-heading text-xl font-semibold">
              Tool<span className="text-primary">Haus</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm">
            <Link to="/" className={`hover:text-primary transition-colors ${loc.pathname === "/" ? "text-primary" : "text-gray-700"}`} data-testid="nav-home">All Tools</Link>
            <Link to="/category/ai" className="text-gray-700 hover:text-primary transition-colors" data-testid="nav-ai">AI</Link>
            <Link to="/category/pdf" className="text-gray-700 hover:text-primary transition-colors" data-testid="nav-pdf">PDF</Link>
            <Link to="/category/image" className="text-gray-700 hover:text-primary transition-colors" data-testid="nav-image">Image</Link>
            <Link to="/category/dev" className="text-gray-700 hover:text-primary transition-colors" data-testid="nav-dev">Developer</Link>
          </nav>
          <div className="flex items-center gap-2">
            <span className="chip hidden sm:inline-flex"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>100% free</span>
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="h-9 w-9 rounded-lg border border-border bg-card flex items-center justify-center text-gray-500 hover:text-primary hover:border-primary/40 transition-colors"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border bg-secondary mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-7 w-7 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
                <Wrench size={14} strokeWidth={2} />
              </div>
              <span className="font-heading text-lg font-semibold">Tool<span className="text-primary">Haus</span></span>
            </div>
            <p className="text-sm text-gray-500 max-w-sm leading-relaxed">
              Your free online toolbox — AI writing, PDF, image, developer and text utilities in one welcoming place. No sign-ups, no watermarks, nothing stored.
            </p>
            <div className="flex items-center gap-3 mt-5 text-gray-400">
              <a href="#" aria-label="Github" className="hover:text-primary transition-colors" data-testid="footer-github"><Github size={18} /></a>
              <a href="#" aria-label="Twitter" className="hover:text-primary transition-colors" data-testid="footer-twitter"><Twitter size={18} /></a>
            </div>
          </div>
          {[
            { title: "Categories", links: [
              { to: "/category/ai", label: "AI Writing" },
              { to: "/category/pdf", label: "PDF Tools" },
              { to: "/category/image", label: "Image Tools" },
              { to: "/category/dev", label: "Developer" },
            ]},
            { title: "Popular", links: [
              { to: "/tool/ai-summarizer", label: "Summarizer" },
              { to: "/tool/merge-pdf", label: "Merge PDF" },
              { to: "/tool/image-compress", label: "Compress Image" },
              { to: "/tool/qr-generator", label: "QR Generator" },
            ]},
            { title: "Site", links: [
              { to: "/privacy", label: "Privacy" },
              { to: "/terms", label: "Terms" },
              { to: "/about", label: "About" },
              { to: "/contact", label: "Contact" },
            ]},
          ].map((col) => (
            <div key={col.title}>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400 mb-3">{col.title}</div>
              <ul className="space-y-2">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-sm text-gray-600 hover:text-primary transition-colors">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-500">© {new Date().getFullYear()} ToolHaus. Built for the open web.</p>
            <p className="text-xs text-gray-400 font-mono">v1.1 · all tools free forever</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
